<template>
  <div class="py-24">
    <PaymentMethodsList
      v-model="selectedPaymentMethod"
      :disabled="props.disabled || !isPaymentRequired"
      :loading="isLoadingAvailableMethods"
      :payment-groups="paymentGroups"
    >
      <InstallmentSimulation
        v-if="selectedPaymentMethod"
        :base-price="basePrice"
        :payment-method="selectedPaymentMethod"
      />

      <PaymentGroupMessages
        v-if="selectedPaymentMethodGroup"
        :group="selectedPaymentMethodGroup"
      />

      <component :is="form.component.is" v-bind="form.component.props" />
    </PaymentMethodsList>

    <component
      :is="form.component.is"
      v-if="!isPaymentRequired"
      v-bind="form.component.props"
    />

    <div class="mt-32 flex justify-end px-24">
      <component
        :is="submitButton.component.is"
        v-bind="submitButton.component.props"
        :disabled="isSubmitDisabled"
        :loading="isFormBusy"
      >
        {{ i18n(translations.submitButton) }}
      </component>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, toRef, watch } from 'vue'

import { HttpEvent, type Price } from '@backmarket/http-api'
import {
  type CreatePaymentResponse,
  type PaymentCreateBody,
  type PaymentMethod,
} from '@backmarket/http-api/src/api-specs-payment/payment'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import InstallmentSimulation from '../../../advertising/components/InstallmentSimulation/InstallmentSimulation.vue'
import {
  getRedirection,
  getRedirectionToPaymentResult,
} from '../../../form-common/helpers/getRedirection'
import {
  type PaymentFormAdapterCreateFunction,
  PaymentSubmitError,
  type SetupErrorEvent,
  type SubmitErrorEvent,
  type SubmitStartEvent,
  type SubmitSuccessEvent,
} from '../../../form-common/types'
import { useAvailablePaymentMethods } from '../../../methods'
import PaymentMethodsList from '../../../methods/components/PaymentMethodsList/PaymentMethodsList.vue'
import { usePaymentMethodGroups } from '../../../methods/composables/usePaymentMethodGroups'
import PaymentGroupMessages from '../PaymentFormGroupMessages/PaymentFormGroupMessages.vue'

import translations from './PaymentForm.translations'
import { getSetupErrorMessage } from './PaymentForm.utils'
import { useFraudData } from './useFraudData'
import { usePaymentFormAdapter } from './usePaymentFormAdapter'
import { usePaymentFormEventTracker } from './usePaymentFormEventTracker'
import { usePaymentNextAction } from './usePaymentNextAction'

const i18n = useI18n()

const props = withDefaults(
  defineProps<{
    basePrice: Price
    createPayment: (body: PaymentCreateBody) => Promise<CreatePaymentResponse>
    disabled?: boolean
    paymentMethods: PaymentMethod[]
    resumePaymentId?: string
  }>(),
  {
    disabled: false,
    resumePaymentId: undefined,
  },
)

const emit = defineEmits<{
  'submit-start': [SubmitStartEvent]
  'submit-success': [SubmitSuccessEvent]
  'submit-error': [SubmitErrorEvent]
  'setup-error': [SetupErrorEvent]

  /**
   * Emitted when the selected payment method changes.
   * Can be used to sync other parts of the UI depending on the current payment method.
   */
  'update:selectedMethod': [
    modelValue: PaymentMethod | null,
    isSelectedByUser: boolean,
  ]
}>()

// Get available payment methods and group them
const { availablePaymentMethods, pending: isLoadingAvailableMethods } =
  useAvailablePaymentMethods(props.paymentMethods)
const paymentGroups = usePaymentMethodGroups(availablePaymentMethods)

// Define which payment method is selected
const defaultPaymentMethod = computed(() => {
  const method = paymentGroups.value[0]?.methods[0]

  return method?.enabled ? method : null
})
const userSelectedPaymentMethodReference = ref<string | null>(null)
const selectedPaymentMethod = computed<PaymentMethod | null>({
  get: () => {
    if (isLoadingAvailableMethods.value) {
      return null
    }

    return userSelectedPaymentMethodReference.value
      ? availablePaymentMethods.value.find(
          (method) =>
            method.reference === userSelectedPaymentMethodReference.value,
        ) ?? defaultPaymentMethod.value
      : defaultPaymentMethod.value
  },
  set: (val) => {
    userSelectedPaymentMethodReference.value = val?.reference ?? null
  },
})

const selectedPaymentMethodGroup = computed(() => {
  if (!selectedPaymentMethod.value) {
    return null
  }

  return paymentGroups.value.find(
    (group) => group.id === selectedPaymentMethod.value?.group,
  )
})

// Handler for the payment creation
const { data: fraudData } = useFraudData(selectedPaymentMethod)
const safelyCreatePayment: PaymentFormAdapterCreateFunction = async (
  paymentMethodConfig?: Record<string, unknown>,
) => {
  const { paymentId, gatewayReference, nextAction } = await props.createPayment(
    {
      payment_method: {
        config: paymentMethodConfig,
        reference: selectedPaymentMethod.value?.reference,
      },
      requested_price: props.basePrice,
      ...fraudData.value,
    },
  )

  return {
    paymentId,
    gatewayReference,
    redirection: nextAction ? getRedirection(nextAction, paymentId) : undefined,
  }
}

// Prepare for tracking
const isPaymentRequired = computed(() => parseFloat(props.basePrice.amount) > 0)
const track = usePaymentFormEventTracker({
  availablePaymentMethods,
  basePrice: () => props.basePrice,
  defaultPaymentMethod,
  isPaymentRequired,
  selectedPaymentMethod,
})

// Get the current form adapter
const formId = computed(() => {
  if (!isPaymentRequired.value) {
    return 'payment-method:none'
  }

  return selectedPaymentMethod.value?.reference
    ? `payment-method:${selectedPaymentMethod.value.reference}`
    : 'payment-method:none'
})
const { form, submitButton, loadingMessage } = usePaymentFormAdapter({
  // Handle form setup
  onSetupStart: () => track.setupStart(),
  onSetupSuccess: () => track.setupSuccess(),
  onSetupError: (error) => {
    const message = getSetupErrorMessage(
      i18n,
      selectedPaymentMethod.value,
      error,
    )
    track.setupError({ data: { error, message } })
    emit('setup-error', {
      paymentMethod: selectedPaymentMethod.value,
      message,
      cause: error,
    })

    // TODO: [PAYIN-3611] Select the next available payment method on setup error
    selectedPaymentMethod.value = null
  },

  // Handle form submission
  onSubmitStart: () => {
    track.submitStart()
    emit('submit-start', {
      paymentMethod: selectedPaymentMethod.value,
      // TODO: [PAYIN-3634] PaymentForm should proxy-expose adapter's custom loadingMessage
      customLoadingMessage: loadingMessage.value,
    })
  },
  onSubmitSuccess: (result) => {
    track.submitSuccess(result)
    emit('submit-success', {
      paymentMethod: selectedPaymentMethod.value,
      ...result,
    })
  },
  onSubmitError: (error) => {
    track.submitError({ error })
    emit('submit-error', {
      paymentMethod: selectedPaymentMethod.value,
      message: error.readableMessage,
      redirection: error.redirection,
      cause: error,
    })
  },

  formId,
  isPaymentRequired,
  basePrice: toRef(props, 'basePrice'),
  paymentMethod: selectedPaymentMethod,
  createPayment: safelyCreatePayment,
})

// Handle resume payment
const { status: paymentNextActionStatus } = usePaymentNextAction(
  toRef(props, 'resumePaymentId'),
  {
    onEvent: (event, context) => {
      switch (event) {
        case HttpEvent.Attempt:
          track.resumeStart()
          emit('submit-start', {
            paymentMethod: selectedPaymentMethod.value,
          })
          break
        case HttpEvent.Success:
          if (context.data?.action === 'SHOW_ERROR') {
            track.resumeError({ data: context.data })
            emit('submit-error', {
              paymentMethod: selectedPaymentMethod.value,
              message: {
                title: context.data.data?.title ?? '',
                description: context.data.data?.detail ?? '',
              },
            } satisfies SubmitErrorEvent)
          } else {
            track.resumeSuccess()
            emit('submit-success', {
              paymentMethod: selectedPaymentMethod.value,
              paymentId: String(props.resumePaymentId),
              redirection: getRedirectionToPaymentResult(props.resumePaymentId),
            } satisfies SubmitSuccessEvent)
          }
          break
        case HttpEvent.Fail:
          // eslint-disable-next-line no-case-declarations
          const error = PaymentSubmitError.fromAnyError(context.error)
          track.resumeError({ error })
          emit('submit-error', {
            paymentMethod: selectedPaymentMethod.value,
            message: error.readableMessage,
            redirection: error.redirection,
            cause: error,
          })
          break
        default:
          break
      }
    },
  },
)

const isFormBusy = computed(
  () => form.isBusy.value || paymentNextActionStatus.value === 'pending',
)
const isSubmitDisabled = computed(
  () => props.disabled || isFormBusy.value || isLoadingAvailableMethods.value,
)

// Track when the form is displayed
watch(isLoadingAvailableMethods, () => {
  // We consider that the form is displayed when the available payment methods are loaded
  // (or failed to load)
  if (!isLoadingAvailableMethods.value) {
    track.formDisplayed()
  }
})

// Track when the selected payment method changes
watch(selectedPaymentMethod, () => {
  const method = selectedPaymentMethod.value

  const isSelectedByUser =
    method?.reference === userSelectedPaymentMethodReference.value

  emit('update:selectedMethod', method, isSelectedByUser)

  track(
    'PAYMENT_FORM_METHOD_SELECTED',
    isSelectedByUser
      ? `Payment method selected by user (${method?.bmCode})`
      : `Default payment method selected (${method?.bmCode})`,
    {
      method,
      selectedByUser: !isSelectedByUser,
    },
  )
})
</script>
